import { useEffect, useState } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  Input,
  Modal,
  Row,
  Skeleton,
  Space,
  Switch,
  Tag,
  Typography,
} from 'antd';
import { ToolOutlined, ReloadOutlined, MailOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { Can } from '../../rbac/RequirePermission';
import { usePermissions } from '../../auth/store';

export interface MaintenanceConfig {
  enabled: boolean;
  title: string;
  message: string;
  description: string;
  supportEmail: string;
  supportButtonText: string;
  secondaryMessage: string;
  restorationMessage: string;
}

interface AdminMaintenanceView extends MaintenanceConfig {
  updatedAt: string | null;
  updatedBy: string | null;
}

type Field = Exclude<keyof MaintenanceConfig, 'enabled'>;

/** Same limits the API enforces (shared-types MAINTENANCE_LIMITS). */
export const LIMITS: Record<Field, number> = {
  title: 80,
  message: 300,
  description: 600,
  supportEmail: 254,
  supportButtonText: 40,
  secondaryMessage: 300,
  restorationMessage: 200,
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Client-side check mirroring the server's; the server still validates. */
export function validateMaintenance(c: MaintenanceConfig): Partial<Record<Field, string>> {
  const e: Partial<Record<Field, string>> = {};
  if (!c.title.trim()) e.title = 'Title is required';
  if (!c.message.trim()) e.message = 'Message is required';
  if (!c.supportButtonText.trim()) e.supportButtonText = 'Button text is required';
  if (!EMAIL_RE.test(c.supportEmail.trim())) e.supportEmail = 'Enter a valid email address';
  (Object.keys(LIMITS) as Field[]).forEach((f) => {
    if (!e[f] && c[f].trim().length > LIMITS[f]) e[f] = `At most ${LIMITS[f]} characters`;
  });
  return e;
}

const FIELDS: { key: Field; label: string; multiline?: boolean; optional?: boolean; placeholder?: string }[] = [
  { key: 'title', label: 'Screen title' },
  { key: 'message', label: 'Main message', multiline: true },
  { key: 'description', label: 'Description', multiline: true, optional: true },
  { key: 'secondaryMessage', label: 'Secondary message', multiline: true, optional: true },
  {
    key: 'restorationMessage',
    label: 'Estimated restoration message',
    optional: true,
    placeholder: "e.g. We'll be back shortly.",
  },
  { key: 'supportEmail', label: 'Support email' },
  { key: 'supportButtonText', label: 'Support button text' },
];

function serverError(e: unknown): { message: string; fields: Record<string, string> } {
  const data = (e as { response?: { data?: { message?: string; fields?: Record<string, string> } } })
    .response?.data;
  return { message: data?.message ?? 'Could not save maintenance settings', fields: data?.fields ?? {} };
}

/**
 * Settings → App configuration → Maintenance mode. ON takes the app offline for
 * every user (the API answers 503 MAINTENANCE and the app shows this content),
 * so turning it on asks for confirmation. Nothing is applied until Save.
 */
export function MaintenanceCard() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const { can } = usePermissions();
  const canEdit = can('maintenance.edit');
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin', 'maintenance'],
    queryFn: async () => (await api.get<AdminMaintenanceView>('/admin/app-config/maintenance')).data,
  });
  const [draft, setDraft] = useState<MaintenanceConfig | null>(null);
  const [serverFields, setServerFields] = useState<Record<string, string>>({});

  useEffect(() => {
    if (data && draft === null) setDraft(pick(data));
  }, [data, draft]);

  const save = useMutation({
    mutationFn: async (body: MaintenanceConfig) =>
      (await api.patch<AdminMaintenanceView>('/admin/app-config/maintenance', body)).data,
    onSuccess: (saved) => {
      message.success(saved.enabled ? 'Saved — maintenance mode is ON' : 'Maintenance settings saved');
      setServerFields({});
      qc.setQueryData(['admin', 'maintenance'], saved);
      setDraft(pick(saved));
    },
    onError: (e) => {
      const err = serverError(e);
      setServerFields(err.fields);
      message.error(err.message);
    },
  });

  if (isLoading) {
    return (
      <Card>
        <Skeleton active paragraph={{ rows: 6 }} />
      </Card>
    );
  }
  if (isError || !data) {
    return (
      <Alert
        type="error"
        showIcon
        message="Could not load maintenance settings"
        action={
          <Button size="small" icon={<ReloadOutlined />} onClick={() => void refetch()}>
            Retry
          </Button>
        }
      />
    );
  }
  if (!draft) return null;

  const errors = { ...serverFields, ...validateMaintenance(draft) } as Partial<Record<Field, string>>;
  const dirty = JSON.stringify(draft) !== JSON.stringify(pick(data));
  const hasErrors = Object.keys(validateMaintenance(draft)).length > 0;
  const set = <K extends keyof MaintenanceConfig>(k: K, v: MaintenanceConfig[K]) => {
    setDraft({ ...draft, [k]: v });
    if (serverFields[k]) setServerFields(({ [k]: _drop, ...rest }) => rest);
  };

  const onSave = () => {
    if (draft.enabled && !data.enabled) {
      Modal.confirm({
        title: 'Turn maintenance mode ON?',
        content:
          'Every user will see the maintenance screen and the app will stop working until you turn it off. Calls already in progress can still be ended, and payments still settle.',
        okText: 'Turn ON',
        okButtonProps: { danger: true },
        onOk: () => save.mutate(draft),
      });
      return;
    }
    save.mutate(draft);
  };

  return (
    <Card
      title={
        <Space>
          <ToolOutlined />
          Maintenance mode
          {data.enabled ? (
            <Tag color="red" data-testid="maintenance-status">
              ON — app is offline
            </Tag>
          ) : (
            <Tag color="green" data-testid="maintenance-status">
              OFF
            </Tag>
          )}
        </Space>
      }
      extra={
        data.updatedAt ? (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Last changed {new Date(data.updatedAt).toLocaleString('en-IN')}
          </Typography.Text>
        ) : null
      }
    >
      <Row gutter={[24, 24]}>
        <Col xs={24} lg={14}>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Space align="center">
              <Switch
                checked={draft.enabled}
                disabled={!canEdit}
                onChange={(v) => set('enabled', v)}
                aria-label="Maintenance mode"
                data-testid="maintenance-switch"
              />
              <Typography.Text strong>{draft.enabled ? 'ON' : 'OFF'}</Typography.Text>
              {draft.enabled !== data.enabled && <Tag color="gold">unsaved</Tag>}
            </Space>
            {FIELDS.map((f) => {
              const Comp = f.multiline ? Input.TextArea : Input;
              return (
                <div key={f.key}>
                  <Typography.Text>
                    {f.label}
                    {f.optional && <Typography.Text type="secondary"> (optional)</Typography.Text>}
                  </Typography.Text>
                  <Comp
                    style={{ marginTop: 4 }}
                    aria-label={f.label}
                    value={draft[f.key]}
                    placeholder={f.placeholder}
                    disabled={!canEdit}
                    status={errors[f.key] ? 'error' : undefined}
                    maxLength={LIMITS[f.key] + 20}
                    showCount={{ formatter: ({ count }) => `${count}/${LIMITS[f.key]}` }}
                    autoSize={f.multiline ? { minRows: 2, maxRows: 5 } : undefined}
                    onChange={(e: { target: { value: string } }) => set(f.key, e.target.value)}
                  />
                  {errors[f.key] && (
                    <Typography.Text type="danger" style={{ fontSize: 12 }} role="alert">
                      {errors[f.key]}
                    </Typography.Text>
                  )}
                </div>
              );
            })}
            <Typography.Paragraph type="secondary" style={{ margin: 0, fontSize: 13 }}>
              Applies within seconds of saving. Admin, webhooks, ending calls and session refresh
              keep working while it is ON, so payments settle and nobody is signed out. Keep the
              wording calm: the service is paused on purpose and accounts and coins are safe.
            </Typography.Paragraph>
            <Can permission="maintenance.edit">
              <Space>
                <Button
                  type="primary"
                  danger={draft.enabled && !data.enabled}
                  disabled={!dirty || hasErrors}
                  loading={save.isPending}
                  onClick={onSave}
                >
                  Save
                </Button>
                <Button
                  disabled={!dirty || save.isPending}
                  onClick={() => {
                    setDraft(pick(data));
                    setServerFields({});
                  }}
                >
                  Reset
                </Button>
              </Space>
            </Can>
          </Space>
        </Col>
        <Col xs={24} lg={10}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Preview (not live — saving is what applies it)
          </Typography.Text>
          <MaintenancePreview config={draft} />
        </Col>
      </Row>
    </Card>
  );
}

function pick(v: MaintenanceConfig): MaintenanceConfig {
  return {
    enabled: v.enabled,
    title: v.title,
    message: v.message,
    description: v.description,
    supportEmail: v.supportEmail,
    supportButtonText: v.supportButtonText,
    secondaryMessage: v.secondaryMessage,
    restorationMessage: v.restorationMessage,
  };
}

/** Mobile app palette (maja_mobile AppTheme) so the preview matches the phone. */
const M = {
  bg: '#0F0408',
  warmText: '#F5E8E2',
  warmDim: '#C8B5B0',
  warmMuted: '#A8908A',
  pink: '#FB3C7F',
  coral: '#FF6B4A',
  gold: '#FFD06A',
  surface: 'rgba(255,255,255,.04)',
  line: 'rgba(255,255,255,.08)',
};

/** Phone-sized rendering of the app's maintenance screen with the draft content. */
export function MaintenancePreview({ config }: { config: MaintenanceConfig }) {
  return (
    <div
      data-testid="maintenance-preview"
      style={{
        marginTop: 8,
        maxWidth: 320,
        marginInline: 'auto',
        aspectRatio: '9 / 17',
        borderRadius: 32,
        border: `6px solid #1d1418`,
        background: `radial-gradient(120% 60% at 50% 0%, rgba(251,60,127,.22), transparent 60%), ${M.bg}`,
        padding: '28px 22px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        gap: 10,
        overflow: 'hidden',
      }}
    >
      <div style={{ color: M.warmText, fontWeight: 800, fontSize: 18, letterSpacing: 0.5 }}>
        Dude<span style={{ color: M.pink }}>.</span>
      </div>
      <div
        style={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${M.pink}, ${M.coral})`,
          display: 'grid',
          placeItems: 'center',
          margin: '8px 0',
          color: 'white',
          fontSize: 28,
        }}
      >
        <ToolOutlined />
      </div>
      <div style={{ color: M.warmText, fontSize: 19, fontWeight: 700, lineHeight: 1.25 }}>
        {config.title || 'Title'}
      </div>
      <div style={{ color: M.warmDim, fontSize: 13, lineHeight: 1.45 }}>{config.message}</div>
      {config.description.trim() && (
        <div style={{ color: M.warmMuted, fontSize: 12, lineHeight: 1.45 }}>{config.description}</div>
      )}
      {config.secondaryMessage.trim() && (
        <div style={{ color: M.warmMuted, fontSize: 12, lineHeight: 1.45 }}>{config.secondaryMessage}</div>
      )}
      {config.restorationMessage.trim() && (
        <div
          style={{
            color: M.gold,
            fontSize: 12,
            padding: '6px 12px',
            borderRadius: 999,
            background: M.surface,
            border: `1px solid ${M.line}`,
          }}
        >
          {config.restorationMessage}
        </div>
      )}
      <div
        style={{
          marginTop: 10,
          width: '100%',
          padding: '11px 0',
          borderRadius: 16,
          background: `linear-gradient(135deg, ${M.pink}, ${M.coral})`,
          color: 'white',
          fontWeight: 700,
          fontSize: 14,
        }}
      >
        {config.supportButtonText || 'Contact Support'}
      </div>
      <div style={{ color: M.warmMuted, fontSize: 11 }}>
        <MailOutlined /> {config.supportEmail}
      </div>
    </div>
  );
}
